// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\planning\DelegateLeavesPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table, Button, Space, Popconfirm, Typography, message, Select, Row, Col, Spin, Alert, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, CheckOutlined } from '@ant-design/icons';
import { fetchDelegateLeaves, deleteDelegateLeave, updateDelegateLeave } from '../../redux/slices/planning/planningThunks';
import { getDelegatesForPlanning } from '../../services/delegateService';
import dayjs from 'dayjs';
import DelegateLeaveFormModal from './DelegateLeaveFormModal';
import { exportDelegateLeaves } from '../../services/planning.service';
import apiClient from '../../services/apiClient';

const { Title } = Typography;
const { Option } = Select;

function DelegateLeavesPage() {
  const dispatch = useDispatch();

  // --- RÉCUPÉRATION DE L'UTILISATEUR ET DU RÔLE ---
  const user = useSelector((state) => state.signin.user);
  const isCommercial = user?.role?.name?.toLowerCase() === "comptable";
  // ------------------------------------------------

  const delegateLeaves = useSelector((state) => state.planning?.delegateLeaves || []);
  const loadingLeaves = useSelector((state) => state.planning?.loading?.delegateLeaves || false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [delegatesList, setDelegatesList] = useState([]);
  const [loadingDelegates, setLoadingDelegates] = useState(true);
  const [errorDelegates, setErrorDelegates] = useState(null);
  const [selectedDelegateFilter, setSelectedDelegateFilter] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setLoadingDelegates(true);
    setErrorDelegates(null);
    getDelegatesForPlanning()
      .then(response => {
        const delegateData = Array.isArray(response?.data) ? response.data : [];
        setDelegatesList(delegateData);
      })
      .catch(() => setErrorDelegates('Erreur chargement liste délégués.'))
      .finally(() => setLoadingDelegates(false));
  }, []);

  const loadLeaves = useCallback(() => {
      const params = selectedDelegateFilter ? { delegateId: selectedDelegateFilter } : {};
      dispatch(fetchDelegateLeaves(params));
  }, [dispatch, selectedDelegateFilter]);

  useEffect(() => {
    loadLeaves();
  }, [loadLeaves]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = selectedDelegateFilter ? { delegateId: selectedDelegateFilter } : {};
      const response = await exportDelegateLeaves(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const filename = `export-conges-${dayjs().format('YYYY-MM-DD')}.csv`;
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success("Export des congés téléchargé !");
    } catch (error) {
      message.error("Une erreur est survenue lors de l'export.");
    } finally {
      setExporting(false);
    }
  }, [selectedDelegateFilter]);

  const handleDelete = (id) => {
    dispatch(deleteDelegateLeave(id))
      .unwrap()
      .then(() => message.success('Congé supprimé avec succès'))
      .catch(() => message.error('Erreur lors de la suppression'));
  };

  const handleApprove = async (id) => {
      try {
          await apiClient.patch(`/api/v1/delegate-leaves/${id}`, { status: 'APPROVED' });
          message.success("Congé validé !");
          loadLeaves(); 
      } catch (error) {
          message.error("Erreur lors de la validation.");
      }
  };

  const showAddModal = () => { setEditingLeave(null); setIsModalVisible(true); };
  const showEditModal = (leave) => { setEditingLeave(leave); setIsModalVisible(true); };
  const handleCancel = () => { setIsModalVisible(false); setEditingLeave(null); };
  const handleFilterChange = (value) => { setSelectedDelegateFilter(value === 'all' || !value ? null : value); };

  const columns = [
     {
      title: 'Délégué',
      key: 'delegate',
       render: (_, record) => record.delegate ? `${record.delegate.firstname} ${record.delegate.lastname}` : 'N/A',
    },
    {
      title: 'Date Début',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (date) => dayjs(date).isValid() ? dayjs(date).format('DD/MM/YYYY') : '?',
      sorter: (a, b) => dayjs(a.start_date).unix() - dayjs(b.start_date).unix(),
      width: 110,
    },
     {
      title: 'Date Fin',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (date) => dayjs(date).isValid() ? dayjs(date).format('DD/MM/YYYY') : '?',
      sorter: (a, b) => dayjs(a.end_date).unix() - dayjs(b.end_date).unix(),
      width: 110,
    },
     {
      title: 'Raison',
      dataIndex: 'reason',
      key: 'reason',
      render: (text) => text || '-',
      ellipsis: true,
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      align: 'center',
      render: (status) => {
          const s = status || 'APPROVED';
          let color = 'green';
          let text = 'Validé';
          if (s === 'PENDING') { color = 'orange'; text = 'En attente'; }
          if (s === 'REJECTED') { color = 'red'; text = 'Refusé'; }
          return <Tag color={color}>{text}</Tag>;
      }
    },
    // --- COLONNE ACTIONS (Conditionnelle) ---
    ...(!isCommercial ? [{
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          {record.status === 'PENDING' && (
              <Button 
                  type="primary" size="small" icon={<CheckOutlined />}
                  onClick={() => handleApprove(record.id)}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  title="Valider la demande"
              />
          )}
          <Button size="small" icon={<EditOutlined />} onClick={() => showEditModal(record)} title="Modifier" />
          <Popconfirm title="Supprimer ce congé ?" onConfirm={() => handleDelete(record.id)} okText="Oui" cancelText="Non">
            <Button size="small" icon={<DeleteOutlined />} danger title="Supprimer" />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
         <Col> <Title level={3}>Gestion des Congés Délégués</Title> </Col>
         <Col>
            <Space>
               <Select
                  style={{ width: 250 }} placeholder="Filtrer par délégué..."
                  loading={loadingDelegates} onChange={handleFilterChange}
                  value={selectedDelegateFilter || 'all'}
                  allowClear showSearch optionFilterProp="children"
               >
                  <Option value="all">Tous les délégués</Option>
                  {delegatesList.map(delegate => (
                     <Option key={delegate.id} value={delegate.id}>
                         {`${delegate.firstname || ''} ${delegate.lastname || ''}`.trim()}
                     </Option>
                  ))}
               </Select>

               {/* BOUTON AJOUTER MASQUÉ SI COMMERCIAL */}
               {!isCommercial && (
                 <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}> 
                   Ajouter un Congé 
                 </Button>
               )}

               <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting}>
                 Exporter
               </Button>
            </Space>
         </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={delegateLeaves}
        loading={loadingLeaves}
        rowKey="id"
        bordered
      />
      
      {isModalVisible && (
        <DelegateLeaveFormModal
          visible={isModalVisible}
          onCancel={handleCancel}
          leave={editingLeave}
        />
      )}
    </>
  );
}

export default DelegateLeavesPage;